import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import apiServices from '../../../../services/apiService';

const ExpandedTenancyRow = ({ row, fetchTenancyData }) => {
    const [tenancyData, setTenancyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!row.UnitId && !row.unitId) {
                setLoading(false);
                setError(true);
                return;
            }
            try {
                const data = await fetchTenancyData(row.UnitId || row.unitId);
                if (data) {
                    setTenancyData(data);
                } else {
                    setError(true);
                }
            } catch (err) {
                console.error("Failed to fetch tenancy details", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [row, fetchTenancyData]);

    if (row.RoleId !== 6 && row.roleId !== 6) {
        return <div className="p-4 text-center text-slate-500">Tenancy details are only available for Tenant Unit Admins.</div>;
    }

    if (loading) return <div className="p-8 text-center text-slate-400"><FontAwesomeIcon icon={faHourglassHalf} className="animate-spin mr-2"/> Loading tenancy details...</div>;
    
    if (error || !tenancyData) return <div className="p-8 text-center text-slate-400">No active tenancy records found for this unit.</div>;

    return (
        <div className="p-6 bg-white m-4 rounded-xl border border-slate-200 shadow-sm animate-in fade-in zoom-in duration-300">
            <h4 className="text-sm font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">Tenancy Overview</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">Start Date</p>
                    <p className="text-sm text-slate-800 font-medium">{tenancyData.TenancyStartDate ? new Date(tenancyData.TenancyStartDate).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">End Date</p>
                    <p className="text-sm text-slate-800 font-medium">{tenancyData.TenancyEndDate ? new Date(tenancyData.TenancyEndDate).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">Monthly Rent</p>
                    <p className="text-sm text-slate-800 font-medium">{tenancyData.MonthlyRentAmount != null ? `₹${tenancyData.MonthlyRentAmount}` : 'Not specified'}</p>
                </div>
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">Security Deposit</p>
                    <p className="text-sm text-slate-800 font-medium">{tenancyData.SecurityDeposit != null ? `₹${tenancyData.SecurityDeposit}` : 'Not specified'}</p>
                </div>
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">Unit</p>
                    <p className="text-sm text-slate-800 font-medium">{tenancyData.UnitName} ({tenancyData.UnitNumber})</p>
                </div>
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">Owner</p>
                    <div className="flex flex-col">
                        <span className="text-sm text-slate-800 font-medium">{tenancyData.OwnerName || 'N/A'} <span className="text-[10px] text-slate-400 font-normal ml-1">({tenancyData.OwnershipType || 'Owned'})</span></span>
                        {tenancyData.OwnerMobile && <span className="text-xs text-slate-500 mt-0.5">{tenancyData.OwnerMobile}</span>}
                    </div>
                </div>
                <div>
                    <p className="text-xs text-slate-400 font-semibold mb-1">Status</p>
                    <span className="px-2 py-1 bg-green-50 text-green-700 border border-green-200 text-xs font-bold rounded-md">{tenancyData.TenancyStatus || 'Active'}</span>
                </div>
            </div>

            {tenancyData.TenancyDocuments && tenancyData.TenancyDocuments.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-100">
                    <h5 className="text-xs font-bold text-slate-800 mb-3">Uploaded Documents</h5>
                    <div className="flex flex-wrap gap-3">
                        {tenancyData.TenancyDocuments.map((doc, idx) => (
                            <a 
                                key={idx} 
                                href={doc.DocumentUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex flex-col p-3 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors w-48 shadow-sm group"
                                title="Click to view full image"
                            >
                                <div className="w-full h-28 mb-3 bg-slate-100 rounded flex items-center justify-center overflow-hidden border border-slate-200 group-hover:border-blue-300">
                                    {doc.DocumentUrl ? (
                                        <img 
                                            src={doc.DocumentUrl} 
                                            alt={doc.DocumentName || 'Document'} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <span className="text-slate-400 text-xs font-medium">No Image</span>
                                    )}
                                </div>
                                <span className="text-xs font-bold text-slate-800 truncate">{doc.DocumentType || "Document"}</span>
                                <span className="text-[11px] text-slate-500 truncate mt-1 font-medium">{doc.DocumentName || "Unnamed"}</span>
                                
                                <div className="mt-2 pt-2 border-t border-slate-100 flex flex-col gap-1">
                                    <span className="text-[10px] text-slate-500 italic line-clamp-2" title={doc.Remarks}>
                                        {doc.Remarks ? `"${doc.Remarks}"` : "No remarks"}
                                    </span>
                                    <span className="text-[9px] text-slate-400 font-medium">
                                        Uploaded: {doc.UploadedOn ? new Date(doc.UploadedOn).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpandedTenancyRow;
